import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useParams, useNavigate } from 'react-router-dom';
import BotonAcceso from '../elementos/BotonAcceso';
import { useTheme } from "../guardarToken/themeContext";
import { UserPlus, Save, ArrowLeft, Shield, Eye, EyeOff } from 'lucide-react';
import { CircularProgress } from "@mui/material";

const apiUrlUsers = process.env.REACT_APP_API_USERS || '/api/users/users/';
const apiUrlSectores = process.env.REACT_APP_API_SECTORES || '/api/sectores/section/';
const apiUrlDirecciones = process.env.REACT_APP_API_DIRECCIONES || '/api/direcciones/addres/';
const apiUrlSedes = process.env.REACT_APP_API_SEDES || '/api/sedes/';

const EditUser = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const { isDarkMode } = useTheme();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    const [usuario, setUsuario] = useState('');
    const [pass, setPass] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [nombreuser, setNombreUser] = useState('');
    const [apellido, setApellido] = useState('');
    const [tipo, setTipo] = useState('');

    // Jerarquía de ubicación
    const [sedeId, setSedeId] = useState('');
    const [direccionId, setDireccionId] = useState('');
    const [sectorId, setSectorId] = useState('');

    const [sedes, setSedes] = useState([]);
    const [direcciones, setDirecciones] = useState([]);
    const [sectores, setSectores] = useState([]);
    const [tipoUsuarioActual, setTipoUsuarioActual] = useState('');

    useEffect(() => {
        const fetchData = async () => {
            try {
                setLoading(true);
                const token = localStorage.getItem('token');
                const [userRes, sedesRes, diresRes, sectoresRes] = await Promise.all([
                    axios.get(`${apiUrlUsers}${id}`, { headers: { Authorization: `Bearer ${token}` } }),
                    axios.get(apiUrlSedes),
                    axios.get(apiUrlDirecciones),
                    axios.get(apiUrlSectores)
                ]);

                const userData = userRes.data;
                const allSectores = sectoresRes.data;
                const allDires = diresRes.data;

                setUsuario(userData.usuario || '');
                setNombreUser(userData.nombreuser || '');
                setApellido(userData.apellido || '');
                setTipo(userData.tipo || '');

                // Solo precargar la sede si está realmente persistida en users.sede_id.
                // Si no está guardada, no la inferimos desde sector/dirección para evitar una UI engañosa.
                const currentSectorId = userData.id_sector;
                let resolvedSedeId = userData.sede_id || '';
                let resolvedDireccionId = '';
                let resolvedSectorId = '';

                if (currentSectorId) {
                    resolvedSectorId = currentSectorId;
                    const sectorObj = allSectores.find(s => s.id_sector == currentSectorId);
                    if (sectorObj) {
                        resolvedDireccionId = sectorObj.id_direccion || '';
                    }
                } else {
                    const sectorObj = allSectores.find(s => s.sector === userData.sector);
                    if (sectorObj) {
                        resolvedSectorId = sectorObj.id_sector;
                    }
                }

                if (!resolvedDireccionId && userData.ubicacion) {
                    const direObj = allDires.find(d => d.direccion === userData.ubicacion);
                    if (direObj) {
                        resolvedDireccionId = direObj.id_direccion;
                    }
                }

                setSectorId(resolvedSectorId);
                setSedeId(resolvedSedeId);
                setDireccionId(resolvedDireccionId);

                setSedes(sedesRes.data);
                setDirecciones(allDires);
                setSectores(allSectores);
                setTipoUsuarioActual(localStorage.getItem('tipoUsuario'));
            } catch (error) {
                console.error('Error fetching data:', error);
                setError('No se pudo cargar la información del usuario');
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [id]);

    const handleUpdate = async (e) => {
        e.preventDefault();
        try {
            const token = localStorage.getItem('token');
            const sectorObj = sectores.find(s => s.id_sector === parseInt(sectorId));
            const direObj = direcciones.find(d => d.id_direccion === parseInt(direccionId));
            const sedeObj = sedes.find(s => s.id === parseInt(sedeId));

            const updateData = {
                usuario,
                nombreuser,
                apellido,
                tipo,
                sede_id: sedeId || null,
                id_sector: sectorId || null,
                sector: sectorObj ? sectorObj.sector : '', // fallback
                ubicacion: direObj ? direObj.direccion : '', // fallback
                sede: sedeObj ? sedeObj.nombre : '' // fallback
            };

            // Solo enviar pass si se cambió (opcional, el back debería manejarlo)
            if (pass) updateData.pass = pass;

            await axios.put(`${apiUrlUsers}${id}`, updateData, {
                headers: { Authorization: `Bearer ${token}` }
            });
            navigate('/admin/AbmUsuarios');
        } catch (error) {
            console.error('Error updating user:', error);
            setError(error.response?.data?.message || 'Error al actualizar el usuario');
        }
    };

    const direccionesFiltradas = direcciones.filter(d => !sedeId || d.sede_id == sedeId);
    const sectoresFiltrados = sectores.filter(s => !direccionId || s.id_direccion == direccionId);

    const getTiposDisponibles = () => {
        if (tipoUsuarioActual === 'superadmin') return ['user', 'admin', 'superadmin', 'invitado', 'direccion'];
        return ['user', 'admin', 'invitado', 'direccion'];
    };

    if (loading) {
        return (
            <div className={`min-h-screen flex items-center justify-center ${isDarkMode ? "bg-slate-950" : "bg-slate-50"}`}>
                <CircularProgress color="primary" />
            </div>
        );
    }

    return (
        <div className={`min-h-screen flex items-center justify-center p-4 lg:p-8 ${isDarkMode ? "bg-slate-950" : "bg-slate-50"}`}>
            <div className={`w-full max-w-4xl ${isDarkMode ? "bg-slate-900 border-slate-800" : "bg-white border-slate-100"} rounded-[2.5rem] shadow-2xl border p-8 lg:p-12 animate-in zoom-in duration-500`}>

                <div className="flex flex-col items-center mb-10">
                    <div className="bg-gradient-to-br from-indigo-500 to-indigo-700 p-5 rounded-3xl shadow-xl shadow-indigo-900/30 mb-5">
                        <UserPlus className="text-white w-10 h-10" />
                    </div>
                    <h2 className="text-3xl font-black tracking-tighter mb-2">Editar Usuario</h2>
                    <p className={`text-[11px] font-black uppercase tracking-[0.2em] ${isDarkMode ? "text-slate-500" : "text-slate-400"}`}>Actualizar perfil: {usuario}</p>
                </div>

                {error && <div className="bg-red-500/10 border border-red-500/20 text-red-500 text-xs font-bold p-4 rounded-xl mb-6 text-center">{error}</div>}

                <form className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6" onSubmit={handleUpdate}>
                    {/* DATOS PERSONALES */}
                    <div className="space-y-6">
                        <h3 className="text-xs font-black uppercase tracking-widest text-indigo-500 border-b border-indigo-500/20 pb-2 mb-4">Información Personal</h3>

                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <label className={`block text-[10px] font-black uppercase tracking-widest mb-2 px-1 ${isDarkMode ? "text-slate-500" : "text-slate-400"}`}>Nombre</label>
                                <input type="text" value={nombreuser} onChange={(e) => setNombreUser(e.target.value)}
                                    className={`w-full px-5 py-3.5 rounded-2xl text-sm font-bold transition-all focus:outline-none focus:ring-2 focus:ring-indigo-500/20 ${isDarkMode ? "bg-slate-950 border-slate-800 text-white" : "bg-slate-50 border-slate-100 text-slate-800"} border`} required />
                            </div>
                            <div>
                                <label className={`block text-[10px] font-black uppercase tracking-widest mb-2 px-1 ${isDarkMode ? "text-slate-500" : "text-slate-400"}`}>Apellido</label>
                                <input type="text" value={apellido} onChange={(e) => setApellido(e.target.value)}
                                    className={`w-full px-5 py-3.5 rounded-2xl text-sm font-bold transition-all focus:outline-none focus:ring-2 focus:ring-indigo-500/20 ${isDarkMode ? "bg-slate-950 border-slate-800 text-white" : "bg-slate-50 border-slate-100 text-slate-800"} border`} required />
                            </div>
                        </div>

                        <div>
                            <label className={`block text-[10px] font-black uppercase tracking-widest mb-2 px-1 ${isDarkMode ? "text-slate-500" : "text-slate-400"}`}>Usuario</label>
                            <input type="text" value={usuario} onChange={(e) => setUsuario(e.target.value)}
                                className={`w-full px-5 py-3.5 rounded-2xl text-sm font-bold transition-all focus:outline-none focus:ring-2 focus:ring-indigo-500/20 ${isDarkMode ? "bg-slate-950 border-slate-800 text-white" : "bg-slate-50 border-slate-100 text-slate-800"} border`} required />
                        </div>

                        <div>
                            <label className={`block text-[10px] font-black uppercase tracking-widest mb-2 px-1 ${isDarkMode ? "text-slate-500" : "text-slate-400"}`}>Contraseña (Dejar vacío para no cambiar)</label>
                            <div className="relative">
                                <input type={showPassword ? "text" : "password"} value={pass} onChange={(e) => setPass(e.target.value)}
                                    className={`w-full px-5 pr-14 py-3.5 rounded-2xl text-sm font-bold transition-all focus:outline-none focus:ring-2 focus:ring-indigo-500/20 ${isDarkMode ? "bg-slate-950 border-slate-800 text-white" : "bg-slate-50 border-slate-100 text-slate-800"} border`} />
                                <button
                                    type="button"
                                    onClick={() => setShowPassword((prev) => !prev)}
                                    className={`absolute right-4 top-1/2 -translate-y-1/2 transition-colors ${isDarkMode ? "text-slate-500 hover:text-indigo-400" : "text-slate-400 hover:text-indigo-600"}`}
                                    aria-label={showPassword ? 'Ocultar contraseña' : 'Mostrar contraseña'}
                                >
                                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                                </button>
                            </div>
                        </div>

                        <div>
                            <label className={`block text-[10px] font-black uppercase tracking-widest mb-2 px-1 ${isDarkMode ? "text-slate-500" : "text-slate-400"}`}>Rol de Usuario</label>
                            <div className="relative">
                                <Shield className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-indigo-500" />
                                <select value={tipo} onChange={(e) => setTipo(e.target.value)}
                                    className={`w-full pl-12 pr-5 py-3.5 rounded-2xl text-sm font-bold appearance-none transition-all focus:outline-none focus:ring-2 focus:ring-indigo-500/20 ${isDarkMode ? "bg-slate-950 border-slate-800 text-white" : "bg-slate-50 border-slate-100 text-slate-800"} border`}>
                                    {getTiposDisponibles().map(tipoOp => (
                                        <option key={tipoOp} value={tipoOp}>{tipoOp.charAt(0).toUpperCase() + tipoOp.slice(1)}</option>
                                    ))}
                                </select>
                            </div>
                        </div>
                    </div>

                    {/* UBICACIÓN Y ESTRUCTURA */}
                    <div className="space-y-6">
                        <h3 className="text-xs font-black uppercase tracking-widest text-emerald-500 border-b border-emerald-500/20 pb-2 mb-4">Ubicación y Sector</h3>

                        <div>
                            <label className={`block text-[10px] font-black uppercase tracking-widest mb-2 px-1 ${isDarkMode ? "text-slate-500" : "text-slate-400"}`}>Sede</label>
                            <select value={sedeId} onChange={(e) => { setSedeId(e.target.value); setDireccionId(''); setSectorId(''); }}
                                className={`w-full px-5 py-3.5 rounded-2xl text-sm font-bold transition-all focus:outline-none focus:ring-2 focus:ring-emerald-500/20 ${isDarkMode ? "bg-slate-950 border-slate-800 text-white" : "bg-slate-50 border-slate-100 text-slate-800"} border`} required>
                                <option value="">Seleccione Sede</option>
                                {sedes.map(s => <option key={s.id} value={s.id}>{s.nombre}</option>)}
                            </select>
                        </div>

                        <div>
                            <label className={`block text-[10px] font-black uppercase tracking-widest mb-2 px-1 ${isDarkMode ? "text-slate-500" : "text-slate-400"}`}>Dirección</label>
                            <select value={direccionId} onChange={(e) => { setDireccionId(e.target.value); setSectorId(''); }}
                                className={`w-full px-5 py-3.5 rounded-2xl text-sm font-bold transition-all focus:outline-none focus:ring-2 focus:ring-emerald-500/20 ${isDarkMode ? "bg-slate-950 border-slate-800 text-white" : "bg-slate-50 border-slate-100 text-slate-800"} border`} required>
                                <option value="">Seleccione Dirección</option>
                                {direccionesFiltradas.map(d => <option key={d.id_direccion} value={d.id_direccion}>{d.direccion}</option>)}
                            </select>
                        </div>

                        <div>
                            <label className={`block text-[10px] font-black uppercase tracking-widest mb-2 px-1 ${isDarkMode ? "text-slate-500" : "text-slate-400"}`}>Sector</label>
                            <select value={sectorId} onChange={(e) => setSectorId(e.target.value)}
                                className={`w-full px-5 py-3.5 rounded-2xl text-sm font-bold transition-all focus:outline-none focus:ring-2 focus:ring-emerald-500/20 ${isDarkMode ? "bg-slate-950 border-slate-800 text-white" : "bg-slate-50 border-slate-100 text-slate-800"} border`} required>
                                <option value="">Seleccione Sector</option>
                                {sectoresFiltrados.map(s => <option key={s.id_sector} value={s.id_sector}>{s.sector}</option>)}
                            </select>
                        </div>

                        <div className="pt-4 flex gap-4">
                            <BotonAcceso variant="accent" type='submit'
                                className="flex-1 bg-indigo-600 hover:bg-indigo-700 text-white font-black text-xs uppercase tracking-widest h-16 rounded-2xl shadow-xl shadow-indigo-900/20 flex items-center justify-center gap-2 transform active:scale-95 transition-all">
                                <Save className="w-5 h-5" /> Actualizar Usuario
                            </BotonAcceso>
                            <BotonAcceso variant="dark" type='button' onClick={() => navigate('/admin/AbmUsuarios')}
                                className={`px-8 ${isDarkMode ? "bg-slate-800 hover:bg-slate-700" : "bg-slate-100 hover:bg-slate-200 text-slate-600"} font-black text-xs uppercase tracking-widest h-16 rounded-2xl flex items-center justify-center gap-2 transition-all`}>
                                <ArrowLeft className="w-5 h-5" /> Volver
                            </BotonAcceso>
                        </div>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default EditUser;
